import { useState, useEffect } from 'react';
import { Document } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Download,
  Eye,
  Edit,
  Trash2,
  Folder,
  Loader2,
  CheckCircle2,
  ShieldCheck,
} from 'lucide-react';
import { getFileIcon } from './utils';
import { useDocumentDetailData } from './useDocumentDetailData';
import DocumentDetailsTab from './DocumentDetailsTab';
import DocumentVersionsTab from './DocumentVersionsTab';
import DocumentHistoryTab from './DocumentHistoryTab';
import CommunicationsTab from './CommunicationsTab';
import { useDocumentConversations } from './useDocumentConversations';
import UploadVersionDialog from './UploadVersionDialog';
import { toast } from 'sonner';

// CR-5330: Document details popup. Mirrors the structure used by
// PODetailModal / ASNDetailModal in the purchasing module — Details tab
// for metadata + an Audit History tab driven by the shared Changelog
// component. File actions (download / preview / edit / delete) live in
// the footer so they're available from either tab without an extra click.
//
// The tab bodies live in DocumentDetailsTab / DocumentVersionsTab /
// DocumentHistoryTab; the data they show is loaded by useDocumentDetailData.
// The <Tabs> block below is the one place tabs are declared.

/** The pop-up's tabs, in display order. */
const DETAIL_TABS = ['details', 'versions', 'history', 'communications'] as const;
type DetailTab = typeof DETAIL_TABS[number];
const isDetailTab = (value: string | null | undefined): value is DetailTab =>
  (DETAIL_TABS as readonly string[]).includes(value ?? '');

interface DocumentDetailModalProps {
  document: Document | null;
  open: boolean;
  onClose: () => void;
  onDownload: (doc: Document) => void;
  onPreview: (doc: Document) => void;
  onEdit?: (doc: Document) => void;
  onDelete?: (doc: Document) => void;
  // CR-5328: parent re-fetches the page after a successful version upload
  // so the new version label / size shows up on the tile/row.
  onAfterVersionUpload?: () => void;
  /** CR-8197: tab to open on (from a notification link). Defaults to Details. */
  initialTab?: string | null;
  /** CR-8197: conversation to open on the Communications tab (owner side). */
  initialCounterpart?: number | null;
}

export default function DocumentDetailModal({
  document,
  open,
  onClose,
  onDownload,
  onPreview,
  onEdit,
  onDelete,
  onAfterVersionUpload,
  initialTab,
  initialCounterpart,
}: DocumentDetailModalProps) {
  const { formatDate } = useFormatters();
  const {
    history,
    historyLoading,
    historyError,
    versions,
    versionsLoading,
    versionsError,
    hasAccepted,
    acceptedAt,
    acceptanceCount,
    accepting,
    acknowledgementMessage,
    isRecipient,
    refreshVersions,
    refreshHistory,
    handleAccept,
  } = useDocumentDetailData(document, open);
  const [uploadVersionOpen, setUploadVersionOpen] = useState(false);
  // CR-8197: loaded on open so the Communications tab can show its unread
  // count before the user goes there.
  const conversations = useDocumentConversations(document?.id, open);
  const unread = conversations.summary?.unread_total ?? 0;
  // Controlled so a notification link can open straight on Communications.
  // An unknown tab name in a link falls back to Details.
  const [tab, setTab] = useState<DetailTab>('details');
  useEffect(() => {
    if (open) setTab(isDetailTab(initialTab) ? initialTab : 'details');
  }, [open, document?.id, initialTab]);
  // Bumped after a version upload so an open conversation shows the new
  // "Version n uploaded" entry without leaving the tab.
  const [commsRefresh, setCommsRefresh] = useState(0);

  // The date of the version on screen right now. Falls back to uploaded_at for
  // rows that pre-date current-version tracking, so a document never renders a
  // blank date while the backfill is still rolling out.
  const currentVersionDate = document?.version_uploaded_at || document?.uploaded_at;

  const handleVersionUploaded = () => {
    setUploadVersionOpen(false);
    if (document) {
      refreshVersions(document.id);
      refreshHistory(document.id);
      // A version uploaded with notes adds an entry to the conversation(s).
      conversations.reload();
      setCommsRefresh((n) => n + 1);
    }
    if (onAfterVersionUpload) onAfterVersionUpload();
    toast.success('New version uploaded');
  };

  if (!document) return null;

  // CR-5376: visibility has THREE states (public/shared/private), not two.
  // The previous `is_public ? 'Public' : 'Private'` collapsed any "shared"
  // doc into "Private", which is what Sarah hit — edit form showed Shared,
  // detail view showed Private. Mirrors the derivation in useDocuments.ts
  // (the source of truth elsewhere in the page).
  const sharedCount = (document.shared_with_company_ids || []).length;
  const visibility = document.is_public
    ? 'Public'
    : sharedCount > 0
      ? 'Shared'
      : 'Private';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      {/* No single description line fits this dialog; tell Radix so explicitly. */}
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col gap-0 p-0" aria-describedby={undefined}>
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-start gap-3">
            <div className="text-muted-foreground mt-1 shrink-0">{getFileIcon(document)}</div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-lg leading-tight break-words">
                {document.title}
              </DialogTitle>
              <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
                {document.category_name && (
                  <span className="flex items-center gap-1">
                    <Folder className="h-3 w-3" />
                    {document.category_name}
                  </span>
                )}
                {document.formatted_file_size && (
                  <span className="tabular-nums">{document.formatted_file_size}</span>
                )}
                <Badge variant={document.is_public ? 'default' : 'outline'} className="text-[10px] py-0 h-4">
                  {visibility}
                </Badge>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* CR-5329: Read & Accept banner. Only renders when the doc
            requires acceptance AND the current viewer is one of the
            intended recipients (CR-5329 round 2: Sarah saw the banner
            on the customer side because the gate didn't check that
            the viewer was actually a supplier in shared_with_companies
            — uploaders shouldn't be asked to acknowledge their own
            documents). The "Accepted" confirmation still renders
            regardless so an uploader who later accepts (e.g. when a
            doc is shared back) doesn't see a stale banner. */}
        {document.requires_acceptance && (isRecipient || hasAccepted) && (
          hasAccepted ? (
            <div className="px-6 py-2.5 border-b bg-green-50 dark:bg-green-950/30 flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />
              <span>
                You marked this document as <strong>Read & Accepted</strong>
                {acceptedAt && (
                  <span className="text-muted-foreground"> on {formatDate(acceptedAt)}</span>
                )}
                .
              </span>
              {acceptanceCount > 0 && (
                <Badge variant="secondary" className="ml-auto text-[10px]">
                  {acceptanceCount} {acceptanceCount === 1 ? 'acceptance' : 'acceptances'}
                </Badge>
              )}
            </div>
          ) : (
            <div className="px-6 py-3 border-b bg-amber-50 dark:bg-amber-950/30 flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">This document requires your acknowledgment.</p>
                {acknowledgementMessage ? (
                  <p className="text-xs text-foreground/80 mt-0.5 whitespace-pre-line" data-testid="acknowledgement-message">
                    {acknowledgementMessage}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Open it, review it, then click Read & Accept below.
                  </p>
                )}
              </div>
              <Button size="sm" onClick={handleAccept} disabled={accepting}>
                {accepting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
                Read & Accept
              </Button>
            </div>
          )
        )}

        <Tabs value={tab} onValueChange={(v) => setTab(isDetailTab(v) ? v : 'details')} className="flex-1 flex flex-col overflow-hidden">
          <div className="px-6 pt-4">
            <TabsList>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="versions">
                Versions
                {versions.length > 0 && (
                  <Badge variant="secondary" className="ml-2 text-[10px] py-0 h-4">
                    {versions.length + 1}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="history">Audit History</TabsTrigger>
              <TabsTrigger value="communications">
                Communications
                {unread > 0 && (
                  <Badge variant="destructive" className="ml-2 text-[10px] py-0 h-4" aria-label={`${unread} unread`}>
                    {unread}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="details" className="flex-1 overflow-y-auto px-6 py-4 mt-0">
            <DocumentDetailsTab
              document={document}
              visibility={visibility}
              currentVersionDate={currentVersionDate}
              hasPreviousVersions={versions.length > 0}
            />
          </TabsContent>

          <TabsContent value="versions" className="flex-1 overflow-y-auto px-6 py-4 mt-0">
            <DocumentVersionsTab
              document={document}
              versions={versions}
              loading={versionsLoading}
              error={versionsError}
              currentVersionDate={currentVersionDate}
              onDownloadCurrent={onDownload}
              onNewVersion={onEdit ? () => setUploadVersionOpen(true) : undefined}
            />
          </TabsContent>

          <TabsContent value="history" className="flex-1 overflow-y-auto px-6 py-4 mt-0">
            <DocumentHistoryTab
              history={history}
              loading={historyLoading}
              error={historyError}
            />
          </TabsContent>

          <TabsContent value="communications" className="flex-1 overflow-y-auto px-6 py-4 mt-0">
            <CommunicationsTab
              documentId={document.id}
              summary={conversations.summary}
              summaryLoading={conversations.loading}
              summaryError={conversations.error}
              onRetrySummary={conversations.reload}
              initialCounterpart={initialCounterpart}
              active={tab === 'communications'}
              onConversationRead={conversations.markReadLocally}
              refreshKey={commsRefresh}
            />
          </TabsContent>
        </Tabs>

        <div className="border-t px-6 py-3 flex items-center justify-end gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => onPreview(document)}>
            <Eye className="h-4 w-4 mr-1.5" />
            Preview
          </Button>
          <Button variant="outline" size="sm" onClick={() => onDownload(document)}>
            <Download className="h-4 w-4 mr-1.5" />
            Download
          </Button>
          {onEdit && (
            <Button variant="outline" size="sm" onClick={() => onEdit(document)}>
              <Edit className="h-4 w-4 mr-1.5" />
              Edit
            </Button>
          )}
          {onDelete && (
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 hover:text-red-700"
              onClick={() => onDelete(document)}
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              Delete
            </Button>
          )}
        </div>
      </DialogContent>

      {/* CR-5328: New-version upload sub-dialog */}
      <UploadVersionDialog
        open={uploadVersionOpen}
        document={document}
        onClose={() => setUploadVersionOpen(false)}
        onSuccess={handleVersionUploaded}
      />
    </Dialog>
  );
}
